/* ==================================================================
   NOSH - FOUNDER FEEDBACK PASS 1 + 2: REWARD-LED CART  (2026-10-01)
   ==================================================================
   Pairs with snippets/nosh-cart-rewards.liquid and
   snippets/nosh-cart-extras.liquid. Liquid is the single source of truth
   for reward maths: this file never recalculates qualifying spend, it only
   reads the values the drawer rendered.

   What it does
     1. Saves Special Instructions (cart.note) and the Gift Message (cart
        attribute "Gift Message") to Shopify: debounced while typing and
        immediately on blur. Unsaved text survives drawer re-renders.
     2. Checkout hold: if a save is still pending when Checkout is
        pressed, the click is held, the save finishes, then the click is
        re-fired so Razorpay Magic Checkout (or Shopify checkout) reads
        the saved cart. The fields also sit inside CartDrawer-Form, so a
        native form submit carries them too.
     3. Keeps the "Nosh Rewards" cart attribute equal to the item rewards
        the drawer shows (Option A entitlement), so the order records
        what Nosh must pack. The "Gift Wrap" attribute is kept the same way,
        because a packing slip omits non-shipping lines.
     4. Gift Wrap: exactly one line. Add and remove re-read /cart.js
        first, duplicates collapse to one, and the wrap is removed when no
        merchandise remains. Dormant until the gift-wrap product exists.
     5. Stops change events from these controls reaching Dawn's
        CartItems, whose change handler treats every change as a
        quantity edit.
     6. Serialises every cart write made with fetch on the page, so a
        note save can never overwrite a quantity change (see below).

   Pass 2 (Route 02) adds
     7. "Almost there": the pre-checkout sheet. One capture-phase click guard
        on #CartDrawer-Checkout decides between (a) showing the sheet,
        (b) holding the click while a save finishes, or (c) doing nothing so
        Razorpay receives the customer's own trusted click. Proceed re-fires
        the click once, with a bypass flag, so Razorpay opens exactly once.
        Every failure path fails OPEN: the customer can always check out.
     8. Complete your order and the sheet rows: keeps the rail's scroll
        position across re-renders, and says "Added to cart" only after the
        cart's merchandise count actually rose.
     9. Gift message character count, and the open state of the Gifting
        panels across re-renders.

   Founder refinements (2026-10-05)
    10. Gift Message exists ONLY while gift wrap is on. Turning wrap off clears
        the "Gift Message" attribute in the SAME cart/update.js request that
        removes the wrap, discards any pending or unsaved text, and a guard
        (syncGiftMessage) clears a stale message that outlives its wrap by any
        other route. A hidden message can never reach checkout.

   Deliberately NOT touched: product-form.js, cart.js, cart-drawer.js,
   nosh-cart.js, Razorpay, the cart page. All cart writes go through one
   promise chain so they never race each other.
   ================================================================== */

(function () {
  'use strict';

  var VERSION = 2;
  if (window.NoshCartRewards && window.NoshCartRewards.version === VERSION) return;

  var SAVE_DELAY = 700;
  var FLUSH_TIMEOUT = 5000;
  var REWARD_ATTR = 'Nosh Rewards';
  var WRAP_ATTR = 'Gift Wrap';
  var GIFT_ATTR = 'Gift Message';
  var GIFT_KEY = 'attr:Gift Message';
  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var SEEN_KEY = 'noshAtSeen';
  var PROCEED_COOLDOWN = 4000;
  var ADDED_SHOWN_FOR = 2600;

  var fields = {};
  var chain = Promise.resolve();
  var inflight = 0;
  var rewardSent = null;
  var wrapFlagSent = null;
  var wrapBusy = false;
  var giftClearing = false;
  var bypassCheckout = false;
  var lastTyped = { key: null, at: 0, start: 0, end: 0 };
  var proceeding = false;
  var railX = 0;
  var pendingAdd = null;
  var addedUntil = 0;
  var openDetails = {};

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Shopify applies overlapping cart writes to the same starting cart, so
  // the later response silently undoes the earlier one. QA reproduced it:
  // Dawn blurs the focused field when a quantity change starts, the blur
  // saved the note, and the quantity change was lost. Every cart write on
  // the page (Dawn, Quick Add, this file) therefore runs one at a time, in
  // the order it was made. Reads are untouched. A stuck request releases
  // the queue after 10 s so nothing can wedge it.
  var CART_WRITE = /[/]cart[/](add|change|update|clear)([.]js)?([?]|$)/;
  var cartWriteChain = Promise.resolve();
  var nativeFetch = window.fetch;
  if (nativeFetch && !nativeFetch.__noshSerialised) {
    var serialisedFetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method !== 'POST' || !CART_WRITE.test(url.split('#')[0])) return nativeFetch.apply(this, arguments);
      var self = this;
      var args = arguments;
      var run = cartWriteChain.then(function () {
        return nativeFetch.apply(self, args);
      });
      var settled = run.then(
        function () {},
        function () {}
      );
      cartWriteChain = Promise.race([
        settled,
        new Promise(function (resolve) {
          window.setTimeout(resolve, 10000);
        }),
      ]);
      return run;
    };
    serialisedFetch.__noshSerialised = true;
    window.fetch = serialisedFetch;
  }

  function postJSON(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || (data && data.status && data.status >= 400)) {
          throw new Error((data && (data.description || data.message)) || 'Cart request failed');
        }
        return data;
      });
    });
  }

  function getCart() {
    return fetch(root() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      return response.json();
    });
  }

  // One chain for every cart write, so saves, wrap changes and reward
  // syncs are applied in the order they were requested.
  function enqueue(task) {
    inflight++;
    var run = chain.then(function () {
      return task();
    });
    chain = run.then(settle, settle);
    function settle() {
      inflight--;
    }
    return run;
  }

  /* ---------------- Special Instructions + Gift Message ---------------- */

  function isField(el) {
    return !!(el && el.matches && el.matches('[data-nosh-cx-field]'));
  }

  function keyOf(el) {
    return el.getAttribute('data-nosh-cx-field') === 'note' ? 'note' : 'attr:' + el.getAttribute('data-key');
  }

  function bodyFor(key, value) {
    if (key === 'note') return { note: value };
    var attributes = {};
    attributes[key.slice(5)] = value;
    return { attributes: attributes };
  }

  function fieldState(el) {
    var key = keyOf(el);
    if (!fields[key]) {
      fields[key] = { value: el.value, saved: el.defaultValue, timer: null, dirty: false };
      fields[key].dirty = fields[key].value !== fields[key].saved;
    }
    return fields[key];
  }

  function giftFieldPresent() {
    return !!document.querySelector('#CartDrawer #NoshGiftMessage');
  }

  function setStatus(key, text, state) {
    var nodes = document.querySelectorAll('[data-nosh-cx-status]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute('data-nosh-cx-status') !== key) continue;
      nodes[i].textContent = text;
      if (state) nodes[i].setAttribute('data-state', state);
      else nodes[i].removeAttribute('data-state');
    }
  }

  function scheduleSave(key) {
    var f = fields[key];
    if (f.timer) window.clearTimeout(f.timer);
    f.timer = window.setTimeout(function () {
      saveField(key);
    }, SAVE_DELAY);
  }

  function saveField(key) {
    var f = fields[key];
    if (!f) return Promise.resolve();
    if (f.timer) {
      window.clearTimeout(f.timer);
      f.timer = null;
    }
    if (f.discard) {
      f.dirty = false;
      return Promise.resolve();
    }
    if (!f.dirty) return Promise.resolve();
    // The gift message can only be saved while its field exists, which is
    // only while gift wrap is on. Never write a message nobody can see.
    if (key === GIFT_KEY && !giftFieldPresent()) {
      f.value = f.saved;
      f.dirty = false;
      return Promise.resolve();
    }

    var value = f.value;
    setStatus(key, 'Saving…');
    return enqueue(function () {
      if (value === f.saved) return null;
      return postJSON('cart/update.js', bodyFor(key, value));
    }).then(
      function () {
        if (f.discard) {
          f.dirty = false;
          return;
        }
        f.saved = value;
        f.dirty = f.value !== f.saved;
        if (!f.dirty) setStatus(key, 'Saved');
      },
      function () {
        f.dirty = true;
        setStatus(key, "Couldn't save yet. We'll try again when you check out.", 'error');
      }
    );
  }

  document.addEventListener('input', function (event) {
    var el = event.target;
    if (!isField(el)) return;
    var key = keyOf(el);
    var f = fieldState(el);
    f.value = el.value;
    f.dirty = f.value !== f.saved;
    lastTyped = { key: key, at: Date.now(), start: el.selectionStart, end: el.selectionEnd };
    updateCounts();
    if (f.dirty) {
      setStatus(key, '');
      scheduleSave(key);
    }
  });

  document.addEventListener('focusout', function (event) {
    if (isField(event.target)) saveField(keyOf(event.target));
  });

  // Capture phase on document runs before Dawn's CartItems (a bubble
  // listener on cart-drawer-items), so its quantity validator never sees
  // these controls. The checkbox's own toggle is a default action and is
  // unaffected by stopPropagation.
  document.addEventListener(
    'change',
    function (event) {
      var el = event.target;
      if (!el || !el.closest || !el.closest('[data-nosh-cx]')) return;
      event.stopPropagation();
      if (el.matches('[data-nosh-cx-wrap]')) toggleWrap(el);
      else if (isField(el)) saveField(keyOf(el));
    },
    true
  );

  /* ---------------- Reward entitlement attribute ---------------- */

  function rewardWanted() {
    var panel = document.querySelector('#CartDrawer [data-nosh-cr]');
    if (!panel) return null;
    return {
      want: panel.getAttribute('data-reward-attr') || '',
      have: panel.getAttribute('data-reward-attr-current') || '',
    };
  }

  function rewardOutOfSync() {
    var r = rewardWanted();
    if (!r) return false;
    return r.want !== (rewardSent !== null ? rewardSent : r.have);
  }

  function syncRewards() {
    if (!rewardOutOfSync()) return Promise.resolve();
    var want = rewardWanted().want;
    rewardSent = want;
    var attributes = {};
    attributes[REWARD_ATTR] = want;
    return enqueue(function () {
      return postJSON('cart/update.js', { attributes: attributes });
    }).catch(function () {
      rewardSent = null;
    });
  }

  /* ---------------- Gift wrap flag attribute ---------------- */

  function wrapFlagWanted() {
    var box = document.querySelector('#CartDrawer [data-nosh-cx]');
    if (!box || !box.hasAttribute('data-wrap-attr-current')) return null;
    var wrapped = Number(box.getAttribute('data-wrap-qty')) > 0 && Number(box.getAttribute('data-merch-qty')) > 0;
    return { want: wrapped ? 'Yes' : '', have: box.getAttribute('data-wrap-attr-current') || '' };
  }

  function wrapFlagOutOfSync() {
    var f = wrapFlagWanted();
    if (!f) return false;
    return f.want !== (wrapFlagSent !== null ? wrapFlagSent : f.have);
  }

  function syncWrapFlag() {
    if (!wrapFlagOutOfSync()) return Promise.resolve();
    var want = wrapFlagWanted().want;
    wrapFlagSent = want;
    var attributes = {};
    attributes[WRAP_ATTR] = want;
    return enqueue(function () {
      return postJSON('cart/update.js', { attributes: attributes });
    }).catch(function () {
      wrapFlagSent = null;
    });
  }

  /* ---------------- Gift message guard ---------------- */

  // Invariant: a Gift Message exists only while gift wrap does. The toggle
  // clears it atomically (applyWrap); this guard covers every other way a wrap
  // can disappear (the last product removed, the /cart page stepper, a cart
  // that already held a stale message). It is stateless on purpose: it reads
  // what the server rendered, and only the in-flight flag stops repeats.
  function giftStale() {
    var box = document.querySelector('#CartDrawer [data-nosh-cx]');
    if (!box || !box.hasAttribute('data-gift-current')) return null;
    if (Number(box.getAttribute('data-wrap-qty')) > 0) return null;
    return (box.getAttribute('data-gift-current') || '') !== '' ? box : null;
  }

  function giftOutOfSync() {
    return !giftClearing && !!giftStale();
  }

  function dropGiftFieldState() {
    var f = fields[GIFT_KEY];
    if (f && f.timer) window.clearTimeout(f.timer);
    delete fields[GIFT_KEY];
  }

  function syncGiftMessage() {
    var box = giftStale();
    if (!box || giftClearing || wrapBusy) return Promise.resolve();
    giftClearing = true;
    dropGiftFieldState();
    var attributes = {};
    attributes[GIFT_ATTR] = '';
    return enqueue(function () {
      return postJSON('cart/update.js', { attributes: attributes });
    }).then(
      function () {
        giftClearing = false;
        var live = document.querySelector('#CartDrawer [data-nosh-cx]');
        if (live) live.setAttribute('data-gift-current', '');
      },
      function () {
        giftClearing = false;
      }
    );
  }

  /* ---------------- Gift wrap ---------------- */

  function wrapConfig() {
    var box = document.querySelector('#CartDrawer [data-nosh-cx]');
    if (!box || !box.getAttribute('data-wrap-variant')) return null;
    return {
      box: box,
      handle: box.getAttribute('data-wrap-handle'),
      variant: Number(box.getAttribute('data-wrap-variant')),
    };
  }

  function renderSections(sections) {
    if (!sections) return;
    var parser = new DOMParser();
    var drawerHtml = sections['cart-drawer'];
    if (drawerHtml) {
      var doc = parser.parseFromString(drawerHtml, 'text/html');
      var fresh = doc.querySelector('.drawer__inner');
      var live = document.querySelector('#CartDrawer .drawer__inner');
      if (fresh && live) live.innerHTML = fresh.innerHTML;
      var drawerEl = document.querySelector('cart-drawer');
      if (drawerEl) drawerEl.classList.toggle('is-empty', !!doc.querySelector('.drawer__inner-empty'));
    }
    var bubbleHtml = sections['cart-icon-bubble'];
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubble) {
      var section = parser.parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
      if (section) bubble.innerHTML = section.innerHTML;
    }
  }

  // Brings the cart to: no wrap when `want` is false or there is no
  // merchandise; otherwise exactly one wrap line with quantity 1.
  function applyWrap(config, want) {
    return getCart().then(function (cart) {
      var wraps = [];
      var merchandise = 0;
      cart.items.forEach(function (item) {
        if (item.handle === config.handle) wraps.push(item);
        else merchandise += item.quantity;
      });

      var updates = {};
      var clearGift = !(want && merchandise > 0);
      var giftHeld = !!(cart.attributes && cart.attributes[GIFT_ATTR]);
      if (want && merchandise > 0) {
        if (wraps.length === 0) {
          return postJSON('cart/add.js', {
            items: [{ id: config.variant, quantity: 1, properties: { _nosh_gift_wrap: 'Yes' } }],
            sections: SECTIONS,
            sections_url: window.location.pathname,
          });
        }
        wraps.forEach(function (item, index) {
          if (index === 0 && item.quantity !== 1) updates[item.key] = 1;
          if (index > 0) updates[item.key] = 0;
        });
      } else {
        wraps.forEach(function (item) {
          updates[item.key] = 0;
        });
      }

      var hasUpdates = Object.keys(updates).length > 0;
      var wipeGift = clearGift && giftHeld;
      if (!hasUpdates && !wipeGift) return null;
      // Wrap off removes the wrap AND the gift message in ONE request, so
      // there is no second write to race and no window where one survives.
      var body = {
        sections: SECTIONS,
        sections_url: window.location.pathname,
      };
      if (hasUpdates) body.updates = updates;
      if (wipeGift) {
        body.attributes = {};
        body.attributes[GIFT_ATTR] = '';
      }
      return postJSON('cart/update.js', body);
    });
  }

  function toggleWrap(input) {
    var config = wrapConfig();
    var want = input.checked;
    if (!config || wrapBusy) {
      input.checked = !want;
      return;
    }
    wrapBusy = true;
    input.disabled = true;
    setStatus('wrap', want ? 'Adding gift wrap…' : 'Removing gift wrap…');

    // Turning wrap off discards the gift message. Its blur-save (queued before
    // this click's change event) still runs first; the clear then lands last.
    // `discard` stops that earlier save from marking the field dirty again, so
    // Checkout can never re-save text the customer can no longer see.
    var hadMessage = false;
    if (!want) {
      var giftBox = document.getElementById('NoshGiftMessage');
      var holder = document.querySelector('#CartDrawer [data-nosh-cx]');
      hadMessage =
        !!(giftBox && giftBox.value) || !!(holder && holder.getAttribute('data-gift-current'));
      var gf = fields[GIFT_KEY];
      if (gf) {
        if (gf.timer) window.clearTimeout(gf.timer);
        gf.timer = null;
        gf.discard = true;
        gf.dirty = false;
      }
    }

    enqueue(function () {
      return applyWrap(config, want);
    })
      .then(
        function (result) {
          if (!want) dropGiftFieldState();
          if (result && result.sections) renderSections(result.sections);
          setStatus(
            'wrap',
            want
              ? 'Gift wrap added'
              : hadMessage
              ? 'Gift wrap removed. Your gift message was cleared.'
              : 'Gift wrap removed'
          );
        },
        function () {
          input.checked = !want;
          var kept = fields[GIFT_KEY];
          if (kept) {
            kept.discard = false;
            var again = document.getElementById('NoshGiftMessage');
            if (again) {
              kept.value = again.value;
              kept.dirty = kept.value !== kept.saved;
            }
          }
          setStatus('wrap', "Couldn't update gift wrap. Please try again.", 'error');
        }
      )
      .then(function () {
        wrapBusy = false;
        var toggle = document.querySelector('#CartDrawer [data-nosh-cx-wrap]');
        if (toggle) {
          toggle.disabled = false;
          var container = document.getElementById('CartDrawer');
          if (typeof window.trapFocus === 'function' && container) window.trapFocus(container, toggle);
          else toggle.focus();
        }
      });
  }

  // Repairs a cart that holds more than one wrap, or a wrap with nothing
  // to wrap (for example after the last product was removed).
  function checkWrapIntegrity() {
    var config = wrapConfig();
    if (!config || wrapBusy) return;
    var wrapQty = Number(config.box.getAttribute('data-wrap-qty')) || 0;
    var merchQty = Number(config.box.getAttribute('data-merch-qty')) || 0;
    if (wrapQty <= 1 && !(wrapQty > 0 && merchQty === 0)) return;

    wrapBusy = true;
    enqueue(function () {
      return applyWrap(config, merchQty > 0);
    })
      .then(function (result) {
        if (merchQty === 0) dropGiftFieldState();
        if (result && result.sections) renderSections(result.sections);
      })
      .catch(function () {})
      .then(function () {
        wrapBusy = false;
      });
  }

  /* ---------------- Re-render hydration ---------------- */

  function hydrate() {
    var els = document.querySelectorAll('#CartDrawer [data-nosh-cx-field]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var key = keyOf(el);
      var f = fields[key];
      if (!f) {
        fieldState(el);
        continue;
      }
      if (f.dirty) {
        // The drawer re-rendered from the server before this text was
        // saved: put the customer's text back and keep its panel open.
        if (el.value !== f.value) el.value = f.value;
        var details = el.closest('details');
        if (details) details.open = true;
        if (lastTyped.key === key && Date.now() - lastTyped.at < 2000 && document.activeElement === document.body) {
          el.focus();
          try {
            el.setSelectionRange(lastTyped.start, lastTyped.end);
          } catch (e) {
            /* not all inputs support selection */
          }
        }
      } else {
        f.saved = el.defaultValue;
        f.value = el.value;
      }
    }
    // No gift field in the drawer means no gift wrap: its text is gone, so its
    // saved/pending state must go too (it could otherwise be re-saved).
    if (fields[GIFT_KEY] && !giftFieldPresent()) dropGiftFieldState();
    syncRewards();
    syncWrapFlag();
    syncGiftMessage();
    checkWrapIntegrity();
    updateCounts();
    restoreDetails();
    restoreRail();
    announceAdded();
    if (sheetIsOpen()) {
      var sheet = sheetEl();
      if (sheet) {
        applySheetState(!sheet.contains(document.activeElement));
        if (proceeding) setProceedBusy(true);
      } else {
        closeSheet(false);
      }
    }
  }

  var hydrateQueued = false;
  function queueHydrate(records) {
    if (records) {
      var relevant = false;
      for (var i = 0; i < records.length; i++) {
        var target = records[i].target;
        if (
          !(
            target &&
            target.closest &&
            target.closest('[data-nosh-cx-status], [data-nosh-added-status], [data-nosh-cx-count]')
          )
        ) {
          relevant = true;
          break;
        }
      }
      if (!relevant) return;
    }
    if (hydrateQueued) return;
    hydrateQueued = true;
    window.setTimeout(function () {
      hydrateQueued = false;
      hydrate();
    }, 0);
  }

  // Dawn re-renders the reward panel on quantity changes and adds. Any
  // other cart update (for example a third-party add) refreshes only the
  // lines and footer, so refresh the panel here too.
  function refreshRewardPanel() {
    return fetch(root() + 'cart?section_id=cart-drawer', { credentials: 'same-origin' })
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        // The pinned panel, the Complete your order rail and the sheet all come
        // from the same server render, so they are swapped together.
        ['[data-nosh-cr]', '[data-nosh-co]', '[data-nosh-at]'].forEach(function (selector) {
          var fresh = doc.querySelector(selector);
          var live = document.querySelector('#CartDrawer ' + selector);
          if (fresh && live) {
            live.replaceWith(fresh);
          } else if (fresh) {
            var anchor =
              selector === '[data-nosh-cr]'
                ? document.querySelector('#CartDrawer cart-drawer-items')
                : selector === '[data-nosh-co]'
                ? document.querySelector('#CartDrawer [data-nosh-cx]')
                : null;
            if (anchor) anchor.parentNode.insertBefore(fresh, anchor);
            else if (selector === '[data-nosh-at]') {
              var inner = document.querySelector('#CartDrawer .drawer__inner');
              if (inner) inner.appendChild(fresh);
            }
          } else if (live) {
            live.remove();
          }
        });
      })
      .catch(function () {});
  }

  // PUB_SUB_EVENTS is a top-level const in constants.js, so it is a global
  // binding but not a window property.
  if (typeof window.subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var source = event && event.source;
      if (source === 'product-form') return;
      // The drawer re-renders itself after its own quantity changes, but the
      // /cart page's lines (#main-cart-items) never touch it, so there the
      // panel and the "Nosh Rewards" attribute would go stale. Refresh them.
      if (source === 'cart-items' && !document.getElementById('main-cart-items')) return;
      refreshRewardPanel();
    });
  }

  /* ---------------- Checkout hold ---------------- */

  function needsFlush() {
    if (inflight > 0) return true;
    if (rewardOutOfSync() || wrapFlagOutOfSync() || giftOutOfSync()) return true;
    for (var key in fields) {
      if (fields[key].dirty || fields[key].timer) return true;
    }
    return false;
  }

  function flushAll() {
    var saves = [];
    for (var key in fields) {
      if (fields[key].dirty || fields[key].timer) saves.push(saveField(key));
    }
    saves.push(syncRewards());
    saves.push(syncWrapFlag());
    saves.push(syncGiftMessage());
    return Promise.all(saves).then(function () {
      return chain;
    });
  }

  // Holds a Checkout click while a save finishes, then re-fires it once.
  function holdAndFlush(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    var released = false;
    function release() {
      if (released) return;
      released = true;
      button.removeAttribute('aria-busy');
      if (!button.isConnected) return;
      bypassCheckout = true;
      try {
        button.click();
      } finally {
        bypassCheckout = false;
      }
    }
    flushAll().then(release, release);
    window.setTimeout(release, FLUSH_TIMEOUT);
  }

  /* ---------------- Almost there (pre-checkout sheet) ---------------- */

  function drawerEl() {
    return document.querySelector('cart-drawer');
  }

  function drawerIsOpen() {
    var d = drawerEl();
    return !!(d && d.classList.contains('active'));
  }

  function sheetRoot() {
    return document.querySelector('#CartDrawer [data-nosh-at]');
  }

  function sheetEl() {
    return document.querySelector('#CartDrawer [data-nosh-at-sheet]');
  }

  // The open state lives on <cart-drawer>, which Dawn never re-renders, so a
  // Quick Add inside the sheet (it replaces the drawer's HTML) keeps the
  // sheet open with the fresh content and no flicker.
  function sheetIsOpen() {
    var d = drawerEl();
    return !!(d && d.hasAttribute('data-nosh-at-open'));
  }

  function trapOn(container, focusEl) {
    if (typeof window.trapFocus === 'function') window.trapFocus(container, focusEl || container);
    else (focusEl || container).focus();
  }

  // Everything behind the sheet is inert while it is open, so keyboard and
  // screen-reader users cannot reach the cart underneath.
  function setInert(on) {
    var inner = document.querySelector('#CartDrawer .drawer__inner');
    if (!inner) return;
    for (var i = 0; i < inner.children.length; i++) {
      var child = inner.children[i];
      if (child.hasAttribute('data-nosh-at')) continue;
      if (on) child.setAttribute('inert', '');
      else child.removeAttribute('inert');
    }
  }

  function applySheetState(focus) {
    var sheet = sheetEl();
    if (!sheet) return;
    setInert(true);
    if (focus) trapOn(sheet, sheet);
  }

  function setProceedBusy(on) {
    var button = document.querySelector('#CartDrawer [data-nosh-at-proceed]');
    if (!button) return;
    var label = button.querySelector('span');
    if (on) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      if (label && !button.hasAttribute('data-label')) {
        button.setAttribute('data-label', label.textContent);
        label.textContent = 'Opening checkout…';
      }
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      if (label && button.hasAttribute('data-label')) {
        label.textContent = button.getAttribute('data-label');
        button.removeAttribute('data-label');
      }
    }
  }

  function openSheet() {
    var d = drawerEl();
    if (!d || !sheetRoot()) return false;
    d.setAttribute('data-nosh-at-open', 'opening');
    window.setTimeout(function () {
      var again = drawerEl();
      if (again && again.getAttribute('data-nosh-at-open') === 'opening') again.setAttribute('data-nosh-at-open', 'true');
    }, 260);
    applySheetState(true);
    return true;
  }

  function closeSheet(restoreFocus) {
    var d = drawerEl();
    if (d) d.removeAttribute('data-nosh-at-open');
    setInert(false);
    proceeding = false;
    setProceedBusy(false);
    if (restoreFocus && drawerIsOpen()) {
      var container = document.getElementById('CartDrawer');
      var checkout = document.getElementById('CartDrawer-Checkout');
      if (container && checkout && !checkout.disabled) trapOn(container, checkout);
    }
  }

  // The sheet shows once per unchanged cart. Coming back from a closed
  // Razorpay window and tapping Checkout again goes straight through: the
  // signature is cart count + total + qualifying spend, so any change to the
  // cart shows the sheet again.
  function signature() {
    var r = sheetRoot();
    return r ? r.getAttribute('data-at-sig') || '' : '';
  }

  function markSeen() {
    var sig = signature();
    if (!sig) return;
    try {
      window.sessionStorage.setItem(SEEN_KEY, sig);
    } catch (e) {
      /* storage can be blocked: the sheet then simply shows every time */
    }
  }

  function alreadySeen() {
    var sig = signature();
    if (!sig) return false;
    try {
      return window.sessionStorage.getItem(SEEN_KEY) === sig;
    } catch (e) {
      return false;
    }
  }

  function shouldShowSheet() {
    if (!drawerIsOpen() || !sheetRoot() || sheetIsOpen()) return false;
    return !alreadySeen();
  }

  function proceedToCheckout() {
    if (proceeding) return;
    var button = document.getElementById('CartDrawer-Checkout');
    if (!button || button.disabled) return;
    proceeding = true;
    markSeen();
    setProceedBusy(true);

    var fired = false;
    function fire() {
      if (fired) return;
      fired = true;
      var target = document.getElementById('CartDrawer-Checkout');
      // Inert would not stop a scripted click, but nothing should depend on it.
      setInert(false);
      if (target && !target.disabled) {
        bypassCheckout = true;
        try {
          target.click();
        } finally {
          bypassCheckout = false;
        }
      }
      window.setTimeout(function () {
        proceeding = false;
        setProceedBusy(false);
        if (sheetIsOpen()) setInert(true);
      }, PROCEED_COOLDOWN);
    }
    flushAll().then(fire, fire);
    window.setTimeout(fire, FLUSH_TIMEOUT);
  }

  // One guard, registered on window in the capture phase so it runs before
  // any listener Razorpay or Dawn attaches to the button, the form or the
  // document. It steps aside (returning without touching the event) whenever
  // it has nothing to add, so the customer's own trusted click reaches
  // Razorpay untouched. Any exception also steps aside.
  window.addEventListener(
    'click',
    function (event) {
      var showSheet = false;
      var flush = false;
      var button = null;
      try {
        button = event.target && event.target.closest && event.target.closest('#CartDrawer-Checkout');
        if (!button || bypassCheckout || button.disabled) return;
        showSheet = shouldShowSheet();
        flush = needsFlush();
      } catch (err) {
        return;
      }
      if (!showSheet && !flush) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (showSheet && openSheet()) return;
      holdAndFlush(button);
    },
    true
  );

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-nosh-at-proceed]')) {
      event.preventDefault();
      proceedToCheckout();
    } else if (t.closest('[data-nosh-at-keep]')) {
      event.preventDefault();
      closeSheet(false);
      var d = drawerEl();
      if (d && typeof d.close === 'function') d.close();
    } else if (t.closest('[data-nosh-at-close]')) {
      event.preventDefault();
      closeSheet(true);
    }
  });

  // Escape returns to the cart. Capture phase on document, so Dawn's own
  // Escape handler on <cart-drawer> never sees it and the drawer stays open.
  document.addEventListener(
    'keyup',
    function (event) {
      if (event.code !== 'Escape' || !sheetIsOpen()) return;
      event.stopPropagation();
      closeSheet(true);
    },
    true
  );

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) closeSheet(false);
  });

  /* ---------------- Quick Add confirmation, rail scroll, panels ---------------- */

  function merchQty() {
    var cx = document.querySelector('#CartDrawer [data-nosh-cx]');
    return cx ? Number(cx.getAttribute('data-merch-qty')) || 0 : 0;
  }

  // Remember the merchandise count when a Quick Add form inside the rail or
  // the sheet is submitted. "Added to cart" appears only once the re-rendered
  // drawer shows a HIGHER count, so a failed add never reports success.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.closest || !form.closest('[data-nosh-co], [data-nosh-at]')) return;
      pendingAdd = { at: Date.now(), merch: merchQty() };
    },
    true
  );

  function setAddedText(text) {
    var nodes = document.querySelectorAll('#CartDrawer [data-nosh-added-status]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== text) nodes[i].textContent = text;
    }
  }

  function announceAdded() {
    if (pendingAdd) {
      if (Date.now() - pendingAdd.at > 8000) {
        pendingAdd = null;
      } else if (merchQty() > pendingAdd.merch) {
        pendingAdd = null;
        addedUntil = Date.now() + ADDED_SHOWN_FOR;
        window.setTimeout(function () {
          if (Date.now() >= addedUntil) setAddedText('');
        }, ADDED_SHOWN_FOR + 50);
      }
    }
    // The drawer can re-render twice for one add, so keep the message until
    // its time is up rather than only on the first render.
    setAddedText(Date.now() < addedUntil ? 'Added to cart' : '');
  }

  function railTrack() {
    return document.querySelector('#CartDrawer .nosh-co [data-rail-track]');
  }

  document.addEventListener(
    'scroll',
    function (event) {
      var t = event.target;
      if (t && t.matches && t.matches('.nosh-co [data-rail-track]')) railX = t.scrollLeft;
    },
    true
  );

  function restoreRail() {
    var track = railTrack();
    if (!track || !railX || Math.abs(track.scrollLeft - railX) < 2) return;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = railX;
    track.style.scrollBehavior = '';
  }

  document.addEventListener(
    'toggle',
    function (event) {
      var d = event.target;
      if (d && d.matches && d.matches('details[data-nosh-cx-details]')) {
        openDetails[d.getAttribute('data-nosh-cx-details')] = d.open;
      }
    },
    true
  );

  function restoreDetails() {
    var list = document.querySelectorAll('#CartDrawer details[data-nosh-cx-details]');
    for (var i = 0; i < list.length; i++) {
      if (openDetails[list[i].getAttribute('data-nosh-cx-details')] === true && !list[i].open) list[i].open = true;
    }
  }

  function updateCounts() {
    var counters = document.querySelectorAll('#CartDrawer [data-nosh-cx-count]');
    for (var i = 0; i < counters.length; i++) {
      var box = document.getElementById(counters[i].getAttribute('data-nosh-cx-count'));
      if (!box) continue;
      var max = Number(counters[i].getAttribute('data-max')) || box.maxLength;
      var text = box.value.length + '/' + max;
      if (counters[i].textContent !== text) counters[i].textContent = text;
      if (box.value.length >= max) counters[i].setAttribute('data-state', 'limit');
      else counters[i].removeAttribute('data-state');
    }
  }

  /* ---------------- Boot ---------------- */

  function boot() {
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl && window.MutationObserver) {
      new MutationObserver(queueHydrate).observe(drawerEl, { childList: true, subtree: true });
      // Closing the drawer (X, overlay, Keep shopping, Escape) also closes
      // the sheet, so it never greets the customer on the next open.
      new MutationObserver(function () {
        if (sheetIsOpen() && !drawerIsOpen()) closeSheet(false);
      }).observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
    }
    hydrate();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.NoshCartRewards = {
    version: VERSION,
    flush: flushAll,
    needsFlush: needsFlush,
    hydrate: hydrate,
    openSheet: openSheet,
    closeSheet: closeSheet,
  };
})();